// src/mocks/handlers/funding/advancesSummaryHandler.ts

import { rest } from 'msw';
import invoicesSummary from '@/mocks/data/fundingInvoicesSummaryData.json';
import { API_ENDPOINTS } from '@/services/api/endpoints';
import { VITE_MSW_BASE_URL } from '@/config';
import {
  IInvoicesSummary,
  TInvoicesSummarySortBy,
} from '@/domains/funding/invoices.types';
import { API_DEFAULT_PAGE_SIZE } from '@/services/api/api.constants';
import { matchesDateRange } from '@/mocks/lib/matchesDateRange';
import { matchesAgencyFilter } from '@/mocks/lib/matchesAgencyFilter';
import { PaginatedResponse } from '@/shared/types/api';
import { isAccessTokenExpired } from '@/mocks/lib/authTokenUtils';

function getAdvancesFieldValue(
  item: IInvoicesSummary,
  field: TInvoicesSummarySortBy,
): number | string | null {
  if (field === 'billing_period') {
    return item.year * 100 + item.period;
  }
  return item[field] as string | number | null;
}

export const fundingInvoicesSummaryHandler = rest.get<
  PaginatedResponse<IInvoicesSummary>
>(
  `${VITE_MSW_BASE_URL}${API_ENDPOINTS.FUNDING_INVOICES_SUMMARY}`,
  (req, res, ctx) => {
    const token = req.headers.get('Authorization');
    if (!token) {
      return res(ctx.status(500), ctx.json({ detail: 'Something went wrong' }));
    }

    if (isAccessTokenExpired()) {
      return res(ctx.status(401), ctx.json({ error: 'Access token expired' }));
    }

    const { customer_uuid } = req.params as { customer_uuid: string };
    const group = invoicesSummary.find((g) => g.uuid === customer_uuid);
    if (!group) {
      return res(
        ctx.status(200),
        ctx.json({ total: 0, page: 1, pages: 1, results: [] }),
      );
    }

    const url = new URL(req.url);
    const params = url.searchParams;

    const status = params.get('statuses');
    const currencies = params.getAll('currencies');
    const networks = params.getAll('networks');
    const sources = params.getAll('sources');
    const agencies = params.getAll('agencies');
    const campaigns = params.getAll('campaigns');
    const event_date_start = params.get('event_date_start');
    const event_date_end = params.get('event_date_end');
    const expected_repayment_date_start = params.get(
      'expected_repayment_date_start',
    );
    const expected_repayment_date_end = params.get(
      'expected_repayment_date_end',
    );
    const ordering = (params.get('ordering') as TInvoicesSummarySortBy) || '';
    const page = parseInt(params.get('page') || '1', 10);
    const page_size = parseInt(
      params.get('page_size') || `${API_DEFAULT_PAGE_SIZE}`,
      10,
    );
    // const search = params.get('search') || '';
    // 1. Filter

    let filtered = (group.results as IInvoicesSummary[]).filter((item) => {
      const matchStatus = status ? item.status === status : true;
      const matchCurrency =
        currencies.length > 0 ? currencies.includes(item.currency) : true;
      const matchSource =
        sources.length > 0 && item.source_name
          ? sources.includes(item.source_name)
          : true;
      const matchAgency = matchesAgencyFilter(item.agency_name, agencies);
      const matchNetwork =
        networks.length > 0 && item.network_name
          ? networks.includes(item.network_name)
          : true;
      const matchCampaign =
        campaigns.length > 0
          ? item.campaign_uuid
            ? campaigns.includes(item.campaign_uuid)
            : false
          : true;
      const matchEventDate = matchesDateRange(
        `${item.year}-${item.period - 1}-01`,
        event_date_start,
        event_date_end,
      );
      const matchExpectedRepaymentDate = matchesDateRange(
        item.expected_repayment_date,
        expected_repayment_date_start,
        expected_repayment_date_end,
      );
      return (
        matchStatus &&
        matchCurrency &&
        matchSource &&
        matchAgency &&
        matchNetwork &&
        matchCampaign &&
        matchEventDate &&
        matchExpectedRepaymentDate
      );
    });

    // 2. Sort
    if (ordering) {
      const isDesc = ordering.startsWith('-');
      const key = (
        isDesc ? ordering.slice(1) : ordering
      ) as TInvoicesSummarySortBy;

      filtered = filtered.sort((a, b) => {
        if (a === null || a === undefined) return 1;
        if (b === null || b === undefined) return -1;
        const aValue = getAdvancesFieldValue(a, key);
        const bValue = getAdvancesFieldValue(b, key);

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return isDesc
            ? bValue.localeCompare(aValue)
            : aValue.localeCompare(bValue);
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return isDesc ? bValue - aValue : aValue - bValue;
        }

        return 0;
      });
    }

    // 3. Paginate
    const total = filtered.length;
    const pages = Math.ceil(total / page_size);
    const start = Math.max(0, (page - 1) * page_size);
    const end = Math.min(start + page_size, total);
    const results = filtered.slice(start, end);

    // 4. Response
    return res(
      ctx.status(200),
      ctx.json({
        total,
        page,
        pages,
        results,
      }),
    );
  },
);
